import React, {FC, useMemo, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import parseISO from 'date-fns/parseISO';
import {MdAdd, MdCancel, MdCheck, MdOpenInNew} from 'react-icons/md';
import PatientCompactBanner from '../../../components/Patient/PatientCompactBanner';
import RowActionMenu, {type RowActionMenuItem} from '../../../components/RowActionMenu';
import SidePanel from '../../../components/SidePanel';
import RequestTimeframes from '../../../enums/RequestTimeframes';
import {Glum} from '../../../puddleglum';
import {AppointmentDate} from '../../../utils/format';
import useVoucherActions from '../hooks/useVoucherActions';
import {bookingRequestOrderDownloadUrl, canViewUploadedOrder} from '../utils/bookingRequestOrder';
import {statusColorForBooking} from '../utils/bookingRequestStatus';
import {
	statusColorForVoucher,
	statusLabelForVoucher,
	voucherCode,
	voucherExpiry,
	voucherServiceName,
	VoucherLike,
} from '../utils/vouchers';
import CancelBookingRequestModal from './CancelBookingRequestModal';
import ExistingCharges from './ExistingCharges';
import AddOrEditBookingRequestForm from './forms/AddOrEditBookingRequestForm';
import TreatmentSearch from './TreatmentSearch';
import VoucherOrderSummary from './VoucherOrderSummary';
import {
	Badge,
	Box,
	Heading,
	Input,
	Table,
	Tag,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	VStack,
} from 'components/ui/chakra';
import ProviderBookingController from 'puddleglum/api/providers/ProviderBookingController';
import VoucherController from 'puddleglum/api/providers/VoucherController';

interface BookingRequestsTableProps {
	disabled?: boolean;
	includeRedeemedVouchers: boolean;
	includeCanceledVouchers: boolean;
	includeCanceledBookingRequests: boolean;
}

type SearchResult =
	| {type: 'booking_request'; data: Glum.Models.BookingRequest}
	| {type: 'voucher'; data: VoucherLike};

const typeColorForResult = (type: SearchResult['type']): 'blue' | 'orange' => {
	if (type === 'booking_request') return 'blue';
	return 'orange';
};

const bookingTimeframe = (bookingRequest: Glum.Models.BookingRequest): string => {
	if (bookingRequest.starts_at) {
		return AppointmentDate(parseISO(bookingRequest.starts_at)) || 'Scheduled';
	}

	return (
		RequestTimeframes[bookingRequest.requested_timeframe as keyof typeof RequestTimeframes] ||
		'As soon as possible'
	);
};

const BookingRequestsByDateAndStatus: FC<BookingRequestsTableProps> = ({
	disabled,
	includeRedeemedVouchers,
	includeCanceledVouchers,
	includeCanceledBookingRequests,
}) => {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState('');
	const [recordToEdit, setRecordToEdit] = useState<Glum.Models.BookingRequest>();
	const [mode, setMode] = useState<'charges' | 'appointment'>('appointment');
	const [bookingToCancel, setBookingToCancel] = useState<Glum.Models.BookingRequest>();
	const trimmedSearch = search.trim();
	const hasSearchQuery = trimmedSearch.length >= 2;
	const {
		data: bookingRequests,
		refetch,
		isLoading: isLoadingBookingRequests,
	} = useQuery(
		['provider-checkin-search-booking-requests', trimmedSearch, includeCanceledBookingRequests],
		async () => {
			const statuses = ['pending', 'confirmed', 'completed'];
			if (includeCanceledBookingRequests) statuses.push('canceled');

			const reply = await ProviderBookingController.index({
				search: trimmedSearch,
				status: statuses.join(','),
			});

			return reply.data;
		},
		{
			enabled: hasSearchQuery,
			refetchOnWindowFocus: false,
		},
	);
	const {
		data: vouchers,
		refetch: refetchVouchers,
		isLoading: isLoadingVouchers,
	} = useQuery(
		[
			'provider-checkin-search-vouchers',
			trimmedSearch,
			includeRedeemedVouchers,
			includeCanceledVouchers,
		],
		async () => {
			const statuses = ['pending_payment', 'active'];
			if (includeRedeemedVouchers) statuses.push('redeemed');
			if (includeCanceledVouchers) statuses.push('canceled');

			const reply = await VoucherController.index({
				search: trimmedSearch,
				status: statuses.join(','),
				per_page: 25,
			});

			return (reply.data.data || []) as VoucherLike[];
		},
		{
			enabled: hasSearchQuery,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);
	const searchResults = useMemo<SearchResult[]>(() => {
		const bookingResults = (bookingRequests || []).map((bookingRequest) => ({
			type: 'booking_request' as const,
			data: bookingRequest,
		}));
		const voucherResults = (vouchers || []).map((voucher) => ({
			type: 'voucher' as const,
			data: voucher,
		}));

		return [...bookingResults, ...voucherResults];
	}, [bookingRequests, vouchers]);

	const handleClose = () => {
		setRecordToEdit(undefined);
		refetch();
		queryClient.invalidateQueries(['todays-booking-requests']);
	};

	const handleSave = () => {
		setRecordToEdit(undefined);
		refetch();
		queryClient.invalidateQueries(['todays-booking-requests']);
	};

	const {redeemVoucher, cancelVoucher, isRedeemingId, isCancelingId} = useVoucherActions({
		onChange: refetchVouchers,
	});

	return (
		<>
			<VStack align='stretch' spacing={3}>
				<Input
					placeholder='Search booking requests and vouchers by name, ID, or voucher code'
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
				{!hasSearchQuery && (
					<Text color='gray.500' fontSize='xs'>
						Enter 2+ characters to search
					</Text>
				)}

				{hasSearchQuery && (
					<>
						<Box
							border='1px solid'
							borderColor='gray.200'
							borderRadius='md'
							overflowX='auto'
						>
							<Table>
								<Thead>
									<Tr>
										<Th>Type</Th>
										<Th>Reference</Th>
										<Th>Patient</Th>
										<Th>Details</Th>
										<Th>Status</Th>
										<Th>Actions</Th>
									</Tr>
								</Thead>
								<Tbody>
									{(isLoadingBookingRequests || isLoadingVouchers) && (
										<Tr>
											<Td colSpan={6}>
												<Text color='gray.500'>Searching...</Text>
											</Td>
										</Tr>
									)}

									{!isLoadingBookingRequests &&
										!isLoadingVouchers &&
										searchResults.length === 0 && (
											<Tr>
												<Td colSpan={6}>
													<Text color='gray.500'>
														No booking requests or vouchers matched your
														search.
													</Text>
												</Td>
											</Tr>
										)}

									{searchResults.map((result) => {
										if (result.type === 'booking_request') {
											const bookingRequest = result.data;
											const bookingRequestActions: RowActionMenuItem[] = [];

											if (bookingRequest.status !== 'canceled') {
												bookingRequestActions.unshift(
													{
														label: 'Manage charges',
														icon: <MdAdd />,
														onClick: () => {
															setMode('charges');
															setRecordToEdit(bookingRequest);
														},
														hidden: ![
															'confirmed',
															'completed',
														].includes(bookingRequest.status),
														isDisabled: disabled,
													},
													{
														label:
															bookingRequest.status === 'pending'
																? 'Confirm appointment'
																: 'Edit appointment',
														icon: <MdCheck />,
														onClick: () => {
															setMode('appointment');
															setRecordToEdit(bookingRequest);
														},
														isDisabled: disabled,
													},
													{
														label: 'Cancel booking',
														icon: <MdCancel />,
														onClick: () =>
															setBookingToCancel(bookingRequest),
														isDisabled: disabled,
														tone: 'danger',
													},
													{
														label: 'View order',
														icon: <MdOpenInNew />,
														onClick: () =>
															window.open(
																bookingRequestOrderDownloadUrl(
																	bookingRequest.id,
																),
																'_blank',
																'noopener,noreferrer',
															),
														hidden: !canViewUploadedOrder(
															bookingRequest,
														),
													},
												);
											}

											return (
												<Tr key={`booking-request-${bookingRequest.id}`}>
													<Td>
														<Tag
															colorScheme={typeColorForResult(
																result.type,
															)}
														>
															Booking Request
														</Tag>
													</Td>
													<Td>BR-{bookingRequest.id}</Td>
													<Td>
														{bookingRequest.user?.first_name}{' '}
														{bookingRequest.user?.last_name}
													</Td>
													<Td>
														<VStack align='start' spacing={1}>
															<Text>
																{bookingRequest.reason || 'N/A'}
															</Text>
															<Text color='gray.500' fontSize='sm'>
																{bookingTimeframe(bookingRequest)}
															</Text>
															{bookingRequest.location && (
																<Tag>
																	{bookingRequest.location.name}
																</Tag>
															)}
														</VStack>
													</Td>
													<Td>
														<Badge
															colorScheme={statusColorForBooking(
																bookingRequest.status,
															)}
														>
															{bookingRequest.status}
														</Badge>
													</Td>
													<Td>
														<RowActionMenu
															ariaLabel={`Actions for booking request ${bookingRequest.id}`}
															items={bookingRequestActions}
														/>
													</Td>
												</Tr>
											);
										}

										const voucher = result.data;

										return (
											<Tr key={`voucher-${voucher.id}`}>
												<Td>
													<Tag
														colorScheme={typeColorForResult(
															result.type,
														)}
													>
														Voucher
													</Tag>
												</Td>
												<Td>{voucherCode(voucher)}</Td>
												<Td>
													{voucher.user?.first_name}{' '}
													{voucher.user?.last_name}
												</Td>
												<Td>
													<VStack align='start' spacing={1}>
														<Text>{voucherServiceName(voucher)}</Text>
														<Text color='gray.500' fontSize='sm'>
															Expires: {voucherExpiry(voucher)}
														</Text>
														<VoucherOrderSummary voucher={voucher} />
													</VStack>
												</Td>
												<Td>
													<Badge
														colorScheme={statusColorForVoucher(
															voucher.status,
														)}
													>
														{statusLabelForVoucher(voucher.status)}
													</Badge>
												</Td>
												<Td>
													<RowActionMenu
														ariaLabel={`Actions for voucher ${voucher.id}`}
														items={[
															{
																label: 'Redeem voucher',
																icon: <MdCheck />,
																onClick: () =>
																	redeemVoucher(voucher),
																isDisabled:
																	voucher.status !== 'active' ||
																	isRedeemingId === voucher.id ||
																	disabled,
															},
															{
																label: 'Cancel voucher',
																icon: <MdCancel />,
																onClick: () =>
																	cancelVoucher(voucher),
																isDisabled:
																	voucher.status !== 'active' ||
																	isCancelingId === voucher.id ||
																	disabled,
																tone: 'danger',
															},
														]}
													/>
												</Td>
											</Tr>
										);
									})}
								</Tbody>
							</Table>
						</Box>
					</>
				)}
			</VStack>
			<SidePanel
				title='Confirm Booking Request'
				isOpen={!!recordToEdit && mode === 'appointment'}
				onClose={handleClose}
			>
				{recordToEdit && (
					<AddOrEditBookingRequestForm
						bookingRequest={recordToEdit}
						fieldsToUpdate={{status: 'confirmed'}}
						onSave={handleSave}
						onClose={handleClose}
					/>
				)}
			</SidePanel>
			<SidePanel
				title='Add Charges'
				isOpen={!!recordToEdit && mode === 'charges'}
				onClose={handleClose}
			>
				{recordToEdit?.user && (
					<Heading flex={1} size='md' color='gray.500'>
						{recordToEdit.user.first_name} {recordToEdit.user.last_name}
					</Heading>
				)}
				{recordToEdit?.user && <PatientCompactBanner patient={recordToEdit.user} />}
				{recordToEdit?.user && (
					<TreatmentSearch user={recordToEdit.user} bookingRequest={recordToEdit} />
				)}
				{recordToEdit?.user && (
					<ExistingCharges user={recordToEdit.user} bookingRequest={recordToEdit} />
				)}
			</SidePanel>
			<CancelBookingRequestModal
				bookingRequest={bookingToCancel}
				onCanceled={() => {
					setBookingToCancel(undefined);
					refetch();
					queryClient.invalidateQueries(['todays-booking-requests']);
				}}
				onClose={() => setBookingToCancel(undefined)}
			/>
		</>
	);
};

export default BookingRequestsByDateAndStatus;
